/**
 * The guide piece: the person a client sees, under the studio's own guide
 * heading. Name, role, credentials, bio and photo, each only where set.
 * Draws nothing when the studio has no guide.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'guide',
  description:
    "The studio's guide under the studio's own heading: photo, name, role, credentials and bio, each where set. Drops out with no guide.",
  placeable: true,
  options: {
    eyebrow: { type: 'string', default: "Who you'll see", maxLength: 60, description: 'The small label above the heading; null for none.' },
    showPhoto: { type: 'boolean', default: true, description: "Show the guide's photo.", showsPart: 'photo' },
  },
  parts: {
    section: { description: 'The whole section.', required: false },
    photo: { description: "The guide's photo.", required: false, role: 'photo' },
    eyebrow: { description: 'The small label above the heading.', required: false },
    heading: { description: "The studio's own heading for its guide.", required: false, role: 'title' },
    name: { description: "The guide's name.", required: false, role: 'title' },
    role: { description: "The guide's role.", required: false },
    facts: { description: "The guide's facts row.", required: false },
    credentials: { description: "The guide's credentials.", required: false },
    bio: { description: "The guide's bio.", required: false },
    paragraph: { description: 'One paragraph of the bio.', required: false },
  },
  actions: {},
  events: {},
});

/** What the guide piece knows. */
export interface GuideState {
  /** loading until the studio's profile is known; error when it failed. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The studio's own heading for its guide, such as "Meet your therapist". */
  readonly heading: string;
  /** The guide's name; null when the studio has no guide (the section drops out). */
  readonly name: string | null;
  /** The guide's photo, or null. */
  readonly photoUrl: string | null;
  /** The guide's role, or null. */
  readonly role: string | null;
  /** The guide's credentials, or null. */
  readonly credentials: string | null;
  /** The bio's paragraphs, split on a blank line; empty when none. */
  readonly bio: readonly string[];
  /** True when the studio also has a story, so this section follows it (and does not carry #about). */
  readonly followsStory: boolean;
}

/** The guide piece has no actions. */
export type GuideActions = Record<string, never>;

/** The guide piece tells its host nothing. */
export type GuideEvents = Record<string, never>;
